/**
 * Converts a Celsius temperature to Fahrenheit for chart display.
 * @param {number} celsius
 * @returns {number|null} Fahrenheit, or null if input is not a finite number
 */
function celsiusToFahrenheit(celsius) {
  const value = Number(celsius);
  if (!Number.isFinite(value)) {
    return null;
  }
  return (value * 9) / 5 + 32;
}

export function normalizeTemperatureUnit(value) {
  const text = String(value ?? '').trim().toLowerCase();
  return text === 'celsius' || text === 'c' ? 'celsius' : 'fahrenheit';
}

export function temperatureSymbol(unit) {
  return normalizeTemperatureUnit(unit) === 'celsius' ? '°C' : '°F';
}

function finiteTemperature(value) {
  if (value == null || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function convertTemperature(value, fromUnit, toUnit) {
  const number = finiteTemperature(value);
  if (number == null) {
    return null;
  }
  const from = normalizeTemperatureUnit(fromUnit);
  const to = normalizeTemperatureUnit(toUnit);
  if (from === to) {
    return number;
  }
  if (from === 'fahrenheit') {
    return ((number - 32) * 5) / 9;
  }
  return (number * 9) / 5 + 32;
}

export function convertTemperatureDelta(value, fromUnit, toUnit) {
  const number = finiteTemperature(value);
  if (number == null) {
    return null;
  }
  const from = normalizeTemperatureUnit(fromUnit);
  const to = normalizeTemperatureUnit(toUnit);
  if (from === to) {
    return number;
  }
  return from === 'fahrenheit' ? (number * 5) / 9 : (number * 9) / 5;
}

export function displayTemperature(value, unit, { from = 'fahrenheit', decimals = 0 } = {}) {
  const converted = convertTemperature(value, from, unit);
  if (converted == null) {
    return null;
  }
  if (decimals > 0) {
    return Number(converted.toFixed(decimals));
  }
  return Math.round(converted);
}

export function formatTemperature(value, unit, {
  from = 'fahrenheit',
  fallback = '—',
  space = false,
  decimals = 0,
} = {}) {
  const shown = displayTemperature(value, unit, { from, decimals });
  if (shown == null) {
    return fallback;
  }
  const text = decimals > 0 ? shown.toFixed(decimals) : String(shown);
  return `${text}${space ? ' ' : ''}${temperatureSymbol(unit)}`;
}

export function formatTemperatureDelta(value, unit, {
  from = 'fahrenheit',
  fallback = '—',
  decimals = 2,
} = {}) {
  const converted = convertTemperatureDelta(value, from, unit);
  if (converted == null) {
    return fallback;
  }
  return converted.toFixed(decimals);
}

/**
 * Normalizes a daily weather API record for the radar chart (Fahrenheit temps).
 * @param {{ date?: string, temp?: number, tempMin?: number, tempMax?: number, precipitationPct?: number, precipitationSum?: number, cloudCoverPct?: number, uvMax?: number }} record
 * @returns {{ date: string, parsedDate: Date, temp: number, minTemp: number, maxTemp: number, precipitationPct: number, precipitationSum: number, cloudCoverPct: number, uvMax: number }|null}
 */
export function normalizeDailyWeatherRecord(record) {
  if (!record?.date) {
    return null;
  }

  const parsedDate = new Date(`${record.date}T12:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const tempF = celsiusToFahrenheit(record.temp);
  const minTempF = celsiusToFahrenheit(record.tempMin);
  const maxTempF = celsiusToFahrenheit(record.tempMax);
  const temp = tempF ?? 0;
  const minTemp = minTempF ?? temp;
  const maxTemp = maxTempF ?? temp;

  return {
    date: record.date,
    parsedDate,
    temp,
    minTemp,
    maxTemp,
    precipitationPct: Number(record.precipitationPct) || 0,
    precipitationSum: Number(record.precipitationSum) || 0,
    cloudCoverPct: Number(record.cloudCoverPct) || 0,
    uvMax: Number(record.uvMax) || 0,
  };
}
